import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { getEquipmentPlanning, updateInventory } from '../../api/equipmentPlanning';
import Loader from '../common/Loader';
import './EquipmentPlanning.css';

const currentYear = DateTime.now().year;
const yearOptions = [];
for (let year = 2024; year <= currentYear + 1; year++) {
  yearOptions.push({ value: year, label: String(year) });
}

const seasonOptions = [
  { value: 'summer', label: 'Summer' },
  { value: 'spring', label: 'Spring' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
];

function EquipmentPlanning() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialYear = searchParams.get('year')
    ? { value: Number(searchParams.get('year')), label: searchParams.get('year') }
    : { value: currentYear, label: String(currentYear) };
  const initialSeason = searchParams.get('season')
    ? seasonOptions.find(s => s.value === searchParams.get('season')) || seasonOptions[0]
    : seasonOptions[0];
  const initialMode = searchParams.get('mode') || 'projected';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [year, setYear] = useState(initialYear);
  const [season, setSeason] = useState(initialSeason);
  const [mode, setMode] = useState(initialMode);
  const [inventoryEdits, setInventoryEdits] = useState({});
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());

  const updateUrlParams = (newSeason, newYear, newMode) => {
    setSearchParams({
      season: newSeason,
      year: String(newYear),
      mode: newMode,
    });
  };

  const handleSeasonChange = (newSeason) => {
    setSeason(newSeason);
    updateUrlParams(newSeason.value, year.value, mode);
  };

  const handleYearChange = (newYear) => {
    setYear(newYear);
    updateUrlParams(season.value, newYear.value, mode);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    updateUrlParams(season.value, year.value, newMode);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getEquipmentPlanning(season.value, year.value, 'camp');
      setData(result);

      const initialEdits = {};
      Object.entries(result.inventory).forEach(([item, inv]) => {
        initialEdits[item] = inv.total;
      });
      setInventoryEdits(initialEdits);
    } catch (error) {
      console.error('Error fetching equipment planning:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [season, year]);

  const handleInventoryChange = (itemName, value) => {
    setInventoryEdits(prev => ({ ...prev, [itemName]: value }));
  };

  const handleInventorySave = async (itemName) => {
    const quantity = parseInt(inventoryEdits[itemName], 10) || 0;
    try {
      await updateInventory(itemName, quantity);
      fetchData();
    } catch (error) {
      console.error('Error updating inventory:', error);
    }
  };

  const formatItemName = (name) => {
    return name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const toggleWeek = (weekStart) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekStart)) {
        next.delete(weekStart);
      } else {
        next.add(weekStart);
      }
      return next;
    });
  };

  if (loading) {
    return <Loader />;
  }

  const gaps = data?.gaps?.[mode] || {};
  const negativeGaps = Object.entries(gaps).filter(([_, g]) => g.gap < 0);

  return (
    <div className="equipment-planning">
      <h1>Equipment Planning</h1>

      <div className="filters">
        <div className="filter-group">
          <label>Season</label>
          <Select
            value={season}
            onChange={handleSeasonChange}
            options={seasonOptions}
            styles={{ container: base => ({ ...base, width: 150 }) }}
          />
        </div>

        <div className="filter-group">
          <label>Year</label>
          <Select
            value={year}
            onChange={handleYearChange}
            options={yearOptions}
            styles={{ container: base => ({ ...base, width: 100 }) }}
          />
        </div>

        <div className="filter-group">
          <label>Mode</label>
          <div className="mode-toggle">
            <button
              className={mode === 'current' ? 'active' : ''}
              onClick={() => handleModeChange('current')}
            >
              Current
            </button>
            <button
              className={mode === 'projected' ? 'active' : ''}
              onClick={() => handleModeChange('projected')}
            >
              Projected
            </button>
          </div>
        </div>
      </div>

      <div className="gap-summary">
        <h3>Equipment Gaps ({mode === 'current' ? 'Based on Registrations' : 'Based on Min Kid Count'})</h3>
        <div className="gap-items">
          {negativeGaps.length === 0 ? (
            <span className="gap-item ok">All equipment needs met!</span>
          ) : (
            negativeGaps.map(([item, g]) => (
              <span key={item} className="gap-item negative">
                {formatItemName(item)}: {g.gap} (need {g.peak}, have {g.available})
              </span>
            ))
          )}
        </div>
      </div>

      <table className="weeks-table">
        <thead>
          <tr>
            <th>Week</th>
            <th>Programs</th>
            <th>Kids</th>
            <th>Equipment Needed</th>
          </tr>
        </thead>
        <tbody>
          {data?.weeks?.map(week => {
            const isExpanded = expandedWeeks.has(week.weekStart);
            return (
              <React.Fragment key={week.weekStart}>
                <tr
                  onClick={() => toggleWeek(week.weekStart)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{isExpanded ? '▼' : '▶'} {week.weekStart} - {week.weekEnd}</td>
                  <td>{week.programs.length}</td>
                  <td>
                    {week.programs.reduce((sum, p) => sum + (mode === 'current' ? parseInt(p.registrationCount, 10) || 0 : parseInt(p.minKidCount, 10) || 0), 0)}
                  </td>
                  <td>
                    {Object.entries(week.equipmentNeeds[mode] || {})
                      .filter(([_, qty]) => qty > 0)
                      .map(([item, qty]) => `${formatItemName(item)}: ${qty}`)
                      .join(', ') || 'None'}
                  </td>
                </tr>
                {isExpanded && week.programs.map(program => (
                  <tr key={program.id} className="program-row">
                    <td style={{ paddingLeft: 30 }}>{program.title}</td>
                    <td>{program.sport}</td>
                    <td>{mode === 'current' ? parseInt(program.registrationCount, 10) || 0 : parseInt(program.minKidCount, 10) || 0}</td>
                    <td>
                      {Object.entries(program.equipment?.[mode] || {})
                        .filter(([_, qty]) => qty > 0)
                        .map(([item, qty]) => `${formatItemName(item)}: ${qty}`)
                        .join(', ') || 'None'}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="inventory-section">
        <h3>Inventory (Edit quantities and press Enter to save)</h3>
        <div className="inventory-grid">
          {Object.keys(data?.inventory || {}).map(itemName => (
            <div key={itemName} className="inventory-item">
              <label>{formatItemName(itemName)}</label>
              <input
                type="number"
                value={inventoryEdits[itemName] || 0}
                onChange={(e) => handleInventoryChange(itemName, e.target.value)}
                onBlur={() => handleInventorySave(itemName)}
                onKeyDown={(e) => e.key === 'Enter' && handleInventorySave(itemName)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EquipmentPlanning;
